import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import {
  HelpCircle,
  Star,
  Crown,
  CheckCircle,
  ArrowRight,
  ShoppingCart,
  Mail,
  MessageCircle,
  Clock,
  Shield,
  Award,
  X as XIcon,
  Zap
} from 'lucide-react';
import * as Icons from 'lucide-react';
import { API_URLS } from '../../config/api';
import { parsePrice, formatPriceWithoutCents } from '../../utils/price';
import MediaDisplay from '../MediaDisplay';

const resolveIcon = (name, fallback = 'CheckCircle') => {
  if (!name && Icons[fallback]) return Icons[fallback];
  if (name && Icons[name]) return Icons[name];
  if (fallback && Icons[fallback]) return Icons[fallback];
  return CheckCircle;
};

const fallbackPackages = [
  {
    code: 'CH121',
    name: 'e-Başlangıç',
    description: 'Yeni e-ticaret siteleri için ekonomik başlangıç',
    price: '$3.99',
    originalPrice: '$10.99',
    features: [
      'Hazır Sanal POS (iyzico)',
      '10 GB SSD Disk Alanı',
      'Sınırsız Trafik',
      '2 Core CPU',
      '1024 MB RAM',
      'Ömür Boyu Ücretsiz SSL',
      'Günlük Yedekleme',
      '50 Adet Standart E-Posta',
      'MySQL Veritabanı',
      'Ücretsiz .STORE Alan Adı'
    ]
  },
  {
    code: 'CH122',
    name: 'e-Girişimci',
    description: 'Küçük ölçekli e-ticaret siteleri için profesyonel başlangıç',
    price: '$4.99',
    originalPrice: '$14.29',
    features: [
      'Hazır Sanal POS (iyzico)',
      '30 GB SSD Disk Alanı',
      'Sınırsız Trafik',
      '3 Core CPU',
      '1024 MB RAM',
      'Ömür Boyu Ücretsiz SSL',
      'Günlük Yedekleme',
      'Sınırsız Standart E-Posta',
      'MySQL Veritabanı',
      'Ücretsiz .STORE Alan Adı'
    ]
  },
  {
    code: 'CH123',
    name: 'e-Uzman',
    description: 'Orta Ölçekli e-ticaret siteleri için ideal çözüm',
    price: '$8.99',
    originalPrice: '$17.99',
    features: [
      'Hazır Sanal POS (iyzico)',
      '100 GB SSD Disk Alanı',
      'Sınırsız Trafik',
      '4 Core CPU',
      '2048 MB RAM',
      'Ömür Boyu Ücretsiz SSL',
      'Günlük Yedekleme',
      'Sınırsız Standart E-Posta',
      '5 Adet Xmail Pro (10 GB)',
      'MySQL Veritabanı',
      'Ücretsiz .STORE Alan Adı'
    ]
  },
  {
    code: 'CH124',
    name: 'e-Profesyonel',
    description: 'Yoğun ürün ve trafik akışı olan e-ticaret siteleri için kurumsal çözüm',
    price: '$14.99',
    originalPrice: '$32.99',
    features: [
      'Hazır Sanal POS (iyzico)',
      'Sınırsız SSD Disk Alanı',
      'Sınırsız Trafik',
      '6 Core CPU',
      '4096 MB RAM',
      'Ömür Boyu Ücretsiz SSL',
      'Günlük Yedekleme',
      'Sınırsız Standart E-Posta',
      '25 Adet Xmail Pro (10 GB)',
      'MySQL Veritabanı',
      'Ücretsiz .STORE Alan Adı'
    ]
  }
];

const fallbackFaq = [
  {
    q: 'E-Ticaret Hosting Nedir?',
    a: 'E-Ticaret, ürün ya da hizmetlerinizi siteniz üzerinden müşterilerinize sunabileceğiniz ve sipariş alabileceğiniz yöntemdir. E-Ticaret Hosting paketleri ile WooCommerce ve iyzico entegrasyonları hazır gelir.'
  },
  {
    q: 'E-Ticaret Hosting Paketi Nasıl Yönetilir?',
    a: 'Müşteri panelinden WordPress ve veritabanına otomatik giriş yapabilir, yedek alabilir ve e-posta hesaplarını yönetebilirsiniz.'
  },
  {
    q: 'iyzico Sanal POS (iyziPOS) Nedir?',
    a: 'iyzico, BDDK lisanslı bir ödeme sistemleri şirketidir. iyziPOS ile peşin/taksit ve farklı para birimleriyle ödeme alınabilir.'
  },
  {
    q: 'Ömür Boyu Ücretsiz SSL Nasıl Aktive Edilir?',
    a: 'E-Ticaret Hosting paketleri ile verilen Positive SSL, paket aktif olduğu sürece otomatik olarak aktiftir.'
  }
];

const DEFAULT_CONTENT = {
  hero: {
    badge: 'E-Ticaret Hosting',
    title: 'E-Ticaret Hosting',
    highlight: 'Hazır Sanal POS ile Hemen Satışa Başlayın',
    description: 'Hazır Sanal POS, WooCommerce entegrasyonu ve ücretsiz temalarla online satışa hemen başlayın.',
    image_url: 'https://cdn.natrocdn.com/ResourceFiles/v1/images/hosting/ecommerce-hosting.png',
    bullets: [
      { icon: 'CheckCircle', text: 'Hazır Sanal POS' },
      { icon: 'CheckCircle', text: 'Ücretsiz SSL' },
      { icon: 'CheckCircle', text: 'Hazır Tema' },
      { icon: 'CheckCircle', text: '7/24 Destek' }
    ]
  },
  packages_section: {
    title: 'E-Ticaret Hosting Paketleri',
    description: 'Hazır sanal POS entegrasyonu ve WooCommerce ile.'
  },
  pos_section: {
    cards: [
      {
        image_url: 'https://cdn.natrocdn.com/ResourceFiles/v1/images/webhosting/ecommerce/iyzico_small_icon.png',
        title: 'Hazır Sanal POS Kurulumu',
        description: 'Tüm bankaların kredi kartlarından ister tek çekim, ister taksitle ödeme alın!',
        secondary_description: 'iyzico ile entegrasyon karmaşası yaşamadan anında ödeme almaya başlayın.'
      },
      {
        image_url: 'https://cdn.natrocdn.com/ResourceFiles/v1/images/webhosting/ecommerce/woocomerce_small_icon.png',
        title: 'Dünyanın Tercihi WooCommerce',
        description: 'WordPress altyapısı ile kolay yönetim, esnek yapı ve geniş eklenti ekosistemi.',
        secondary_description: 'Ürünlerinizi ekleyin, siparişlerinizi ve stoklarınızı tek ekrandan yönetin.'
      }
    ]
  },
  highlights_section: {
    title: 'En Pratik ve En Ekonomik E-Ticaret Çözümü',
    subtitle: 'Tasarım ve Sanal POS kurulumu ile uğraşmadan anında yayın',
    cards: [
      {
        icon: 'ShoppingCart',
        title: 'Hazır Sanal POS Kurulumu',
        description: 'iyzico ile tüm kredi kartlarından tek çekim veya taksitle ödeme alın.'
      },
      {
        icon: 'Star',
        title: 'Ücretsiz E-Ticaret Temaları',
        description: 'WooCommerce ile hazır temalardan dilediğinizi seçip kullanın.'
      },
      {
        icon: 'Shield',
        title: 'Ömürboyu Ücretsiz SSL',
        description: 'Hizmet aktif olduğu sürece Positive SSL otomatik yenilensin.'
      }
    ]
  },
  faq_section: {
    title: 'Sıkça Sorulan Sorular',
    subtitle: 'E-ticaret Hosting ile ilgili merak ettiklerinizi burada bulabilirsiniz'
  },
  cta_section: {
    title: 'E-Ticaret Hosting İhtiyacınız İçin Bize Ulaşın',
    description: 'Profesyonel e-ticaret hosting çözümlerimiz hakkında detaylı bilgi almak ve size özel paket önerileri için ekibimizle iletişime geçin.',
    primary_button: { text: 'İletişime Geç', link: '/iletisim', icon: 'Mail' },
    secondary_button: {
      text: 'WhatsApp İletişim',
      action: 'whatsapp',
      icon: 'MessageCircle',
      phone: '905324404011',
      prefill_message: 'Merhaba, E-ticaret hosting hizmetleriniz hakkında bilgi almak istiyorum.'
    },
    highlights: [
      { icon: 'Clock', title: '7/24 Destek', description: 'E-ticaret uzmanlığı ile kesintisiz destek' },
      { icon: 'Shield', title: 'Hazır Sanal POS', description: 'iyzico entegrasyonu ile hızlı satış' },
      { icon: 'Award', title: 'Uzman Ekip', description: 'E-ticaret konusunda deneyimli ekibimiz' }
    ]
  }
};

const EcommerceHostingPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [faq, setFaq] = useState([]);
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchPackages();
    fetchFaq();
    fetchSettings();
    fetchContent();
  }, []);

  const decode = (str) => {
    if (!str) return str;
    return str
      .replace(/Ä±/g, 'ı')
      .replace(/Ä°/g, 'İ')
      .replace(/ÄŸ/g, 'ğ')
      .replace(/Ã¼/g, 'ü')
      .replace(/Ã§/g, 'ç')
      .replace(/Ã¶/g, 'ö')
      .replace(/Ã‡/g, 'Ç')
      .replace(/Ã–/g, 'Ö')
      .replace(/Ãœ/g, 'Ü')
      .replace(/ÅŸ/g, 'Ş')
      .replace(/ÄŸ/g, 'Ğ')
      .replace(/yÄ±l/g, 'yıl');
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_URLS.ECOMMERCE_HOSTING_PACKAGES);
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Ecommerce hosting packages JSON parse failed:', e, text);
        data = {};
      }
      const list = Array.isArray(data?.data) ? data.data : [];
      const mapped = list
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map((p, idx) => {
          const rawFeatures = Array.isArray(p.features)
            ? p.features
            : p.features
            ? (() => {
                try {
                  return JSON.parse(p.features);
                } catch (_) {
                  return [];
                }
              })()
            : [];
          const features = rawFeatures.map((f) => {
            if (typeof f === 'string') {
              const text = decode(f);
              return { name: text, text, included: true };
            }
            if (f && typeof f === 'object') {
              return {
                name: decode(f.name || f.text || ''),
                text: decode(f.text || f.name || ''),
                included: f.included !== false
              };
            }
            return { name: decode(String(f)), text: decode(String(f)), included: true };
          });

          return {
            id: p.id || idx,
            name: decode(p.name || p.title || ''),
            description: decode(p.description || ''),
            price: p.price || '',
            originalPrice: p.original_price || '',
            period: p.period || '/ay',
            featured: p.featured == 1,
            features
          };
        });
      setPackages(mapped.length ? mapped : fallbackPackages);
    } catch (e) {
      console.error('Ecommerce hosting packages fetch failed:', e);
      setPackages(fallbackPackages);
    } finally {
      setLoading(false);
    }
  };

  const fetchFaq = async () => {
    try {
      const res = await fetch(API_URLS.FAQ);
      const data = await res.json();
      const cats = data.categories || [];
      const qs = data.questions || [];
      const targetCat =
        cats.find((c) => (c.title || '').toLowerCase().includes('e-ticaret hosting')) ||
        cats.find((c) => (c.title || '').toLowerCase().includes('e-ticaret')) ||
        null;
      let selected = [];
      if (targetCat) {
        selected = qs
          .filter((q) => q.category_id == targetCat.id && (q.is_active == null || q.is_active === 1))
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((q) => ({ q: decode(q.question), a: decode(q.answer) }));
      }
      setFaq(selected.length ? selected : fallbackFaq);
    } catch (e) {
      console.error('FAQ fetch failed:', e);
      setFaq(fallbackFaq);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
      if (res.ok) {
        const json = await res.json();
        if (json.status === 'success' && json.data?.whatsapp) {
          setWhatsappNumber(json.data.whatsapp);
        }
      }
    } catch (e) {
      console.error('WhatsApp numarası alınamadı:', e);
    }
  };

  const fetchContent = async () => {
    try {
      const res = await fetch(API_URLS.PAGE_CONTENT('eticaret-hosting'));
      const json = await res.json();
      if (json.status === 'success' && json.data) {
        const additionalRaw = json.data.additional_sections;
        let additional = {};
        if (typeof additionalRaw === 'string' && additionalRaw.trim().startsWith('{')) {
          try {
            additional = JSON.parse(additionalRaw);
          } catch (err) {
            console.error('E-ticaret hosting additional_sections parse error:', err);
            additional = {};
          }
        } else if (additionalRaw && typeof additionalRaw === 'object') {
          additional = additionalRaw;
        }
        setPageContent({
          ...json.data,
          additional_sections: additional
        });
      } else {
        setPageContent(null);
      }
    } catch (error) {
      console.error('E-ticaret hosting içeriği alınamadı:', error);
      setPageContent(null);
    }
  };

  const derivedContent = useMemo(() => {
    const additionalRaw = pageContent?.additional_sections;
    const additional = additionalRaw && typeof additionalRaw === 'object' ? additionalRaw : {};

    const heroAdditional = additional.hero && typeof additional.hero === 'object' ? additional.hero : {};
    const packagesSection = additional.packages_section && typeof additional.packages_section === 'object'
      ? additional.packages_section
      : {};
    const posSection = additional.pos_section && typeof additional.pos_section === 'object'
      ? additional.pos_section
      : {};
    const highlightsSection = additional.highlights_section && typeof additional.highlights_section === 'object'
      ? additional.highlights_section
      : {};
    const faqSection = additional.faq_section && typeof additional.faq_section === 'object'
      ? additional.faq_section
      : {};
    const ctaSection = additional.cta_section && typeof additional.cta_section === 'object'
      ? additional.cta_section
      : {};

    return {
      hero: {
        badge: pageContent?.hero_badge_text || DEFAULT_CONTENT.hero.badge,
        title: pageContent?.hero_title || DEFAULT_CONTENT.hero.title,
        highlight: heroAdditional.highlight_text || DEFAULT_CONTENT.hero.highlight,
        description: pageContent?.hero_description || DEFAULT_CONTENT.hero.description,
        image_url: heroAdditional.image_url || pageContent?.hero_image_url || DEFAULT_CONTENT.hero.image_url,
        bullets: Array.isArray(heroAdditional.bullets) && heroAdditional.bullets.length
          ? heroAdditional.bullets
          : DEFAULT_CONTENT.hero.bullets
      },
      packages_section: {
        title: packagesSection.title || DEFAULT_CONTENT.packages_section.title,
        description: packagesSection.description || DEFAULT_CONTENT.packages_section.description,
      },
      pos_section: {
        cards:
          Array.isArray(posSection.cards) && posSection.cards.length
            ? posSection.cards
            : DEFAULT_CONTENT.pos_section.cards
      },
      highlights_section: {
        title: highlightsSection.title || DEFAULT_CONTENT.highlights_section.title,
        subtitle: highlightsSection.subtitle || DEFAULT_CONTENT.highlights_section.subtitle,
        cards:
          Array.isArray(highlightsSection.cards) && highlightsSection.cards.length
            ? highlightsSection.cards
            : DEFAULT_CONTENT.highlights_section.cards
      },
      faq_section: {
        title: faqSection.title || DEFAULT_CONTENT.faq_section.title,
        subtitle: faqSection.subtitle || DEFAULT_CONTENT.faq_section.subtitle
      },
      cta_section: {
        title: ctaSection.title || DEFAULT_CONTENT.cta_section.title,
        description: ctaSection.description || DEFAULT_CONTENT.cta_section.description,
        primary_button: ctaSection.primary_button || DEFAULT_CONTENT.cta_section.primary_button,
        secondary_button: ctaSection.secondary_button || DEFAULT_CONTENT.cta_section.secondary_button,
        highlights:
          Array.isArray(ctaSection.highlights) && ctaSection.highlights.length
            ? ctaSection.highlights
            : DEFAULT_CONTENT.cta_section.highlights
      }
    };
  }, [pageContent]);

  const heroBullets = useMemo(() => {
    const bullets = Array.isArray(derivedContent.hero.bullets) ? derivedContent.hero.bullets : [];
    return bullets
      .map((bullet) => {
        if (typeof bullet === 'string') {
          const text = bullet.trim();
          return text ? { text, icon: 'CheckCircle' } : null;
        }
        if (bullet && typeof bullet === 'object') {
          const text = (bullet.text || bullet.title || '').toString().trim();
          if (!text) return null;
          return { text, icon: bullet.icon || 'CheckCircle' };
        }
        return null;
      })
      .filter(Boolean);
  }, [derivedContent.hero.bullets]);

  const posCards = useMemo(() => {
    const cards = Array.isArray(derivedContent.pos_section.cards)
      ? derivedContent.pos_section.cards
      : [];
    return cards
      .map((card) => ({
        image_url: card?.image_url,
        title: (card?.title || '').toString().trim(),
        description: (card?.description || '').toString().trim(),
        secondary_description: (card?.secondary_description || '').toString().trim()
      }))
      .filter((card) => card.title || card.description || card.secondary_description || card.image_url);
  }, [derivedContent.pos_section.cards]);

  const highlightCards = useMemo(() => {
    const cards = Array.isArray(derivedContent.highlights_section.cards)
      ? derivedContent.highlights_section.cards
      : [];
    return cards
      .map((card) => ({
        icon: card?.icon,
        title: (card?.title || '').toString().trim(),
        description: (card?.description || '').toString().trim()
      }))
      .filter((card) => card.title || card.description || card.icon);
  }, [derivedContent.highlights_section.cards]);

  const ctaHighlights = useMemo(() => {
    const highlights = Array.isArray(derivedContent.cta_section.highlights)
      ? derivedContent.cta_section.highlights
      : [];
    return highlights
      .map((highlight) => ({
        icon: highlight?.icon,
        title: (highlight?.title || '').toString().trim(),
        description: (highlight?.description || '').toString().trim()
      }))
      .filter((highlight) => highlight.title || highlight.description || highlight.icon);
  }, [derivedContent.cta_section.highlights]);

  const primaryButton = useMemo(() => {
    const btn = derivedContent.cta_section.primary_button;
    if (!btn || typeof btn !== 'object') return null;
    const text = (btn.text || '').toString().trim();
    const link = (btn.link || '').toString().trim();
    if (!text || !link) return null;
    return { ...btn, text, link };
  }, [derivedContent.cta_section.primary_button]);

  const secondaryButton = useMemo(() => {
    const btn = derivedContent.cta_section.secondary_button;
    if (!btn || typeof btn !== 'object') return null;
    const text = (btn.text || '').toString().trim();
    if (!text) return null;
    return {
      ...btn,
      text,
      link: (btn.link || '').toString().trim(),
      action: (btn.action || '').toString().trim(),
      phone: (btn.phone || '').toString().trim(),
      prefill_message: (btn.prefill_message || '').toString()
    };
  }, [derivedContent.cta_section.secondary_button]);

  const showPrimaryButton = Boolean(primaryButton);
  const showSecondaryButton = Boolean(secondaryButton);

  const hasHeroContent = Boolean(
    derivedContent.hero.badge ||
      derivedContent.hero.title ||
      derivedContent.hero.highlight ||
      derivedContent.hero.description ||
      derivedContent.hero.image_url ||
      heroBullets.length
  );

  const showPackagesHeader = Boolean(
    derivedContent.packages_section.title || derivedContent.packages_section.description
  );

  const hasPosSection = posCards.length > 0;
  const hasHighlightsSection = Boolean(
    derivedContent.highlights_section.title ||
      derivedContent.highlights_section.subtitle ||
      highlightCards.length
  );

  const hasCTASection = Boolean(
    derivedContent.cta_section.title ||
      derivedContent.cta_section.description ||
      showPrimaryButton ||
      showSecondaryButton ||
      ctaHighlights.length
  );

  const PackageCard = ({ pkg, index }) => {
    const [isHovered, setIsHovered] = useState(false);

    const normalizedFeatures = (pkg.features || []).map((f) => {
      if (typeof f === 'string') {
        return { name: f, text: f, included: true };
      }
      if (f && typeof f === 'object') {
        return {
          name: f.name || f.text || '',
          text: f.text || f.name || '',
          included: f.included !== false
        };
      }
      return { name: String(f), text: String(f), included: true };
    });

    const priceNum = parsePrice(pkg.price);
    const originalPriceNum = parsePrice(pkg.originalPrice);
    const discount = originalPriceNum > 0 ? Math.round(((originalPriceNum - priceNum) / originalPriceNum) * 100) : 0;

    return (
      <Card
        className={`relative border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 cursor-pointer group flex flex-col h-full ${
          pkg.featured ? 'ring-4 ring-emerald-200' : ''
        } bg-white`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          animationDelay: `${index * 0.1}s`,
          animation: 'fadeInUp 0.6s ease-out forwards'
        }}
      >
        {pkg.featured && (
          <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold animate-pulse" style={{ width: '150px' }}>
              <Crown className="w-4 h-4 mr-1" />
              EN POPÜLER
            </Badge>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-green-500 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>

        <CardHeader className="space-y-4 pb-6">
          <div className="space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
              <ShoppingCart className="w-8 h-8 text-white animate-pulse" />
            </div>

            <div className="space-y-2">
              <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                {pkg.name}
              </CardTitle>
              {pkg.description && (
                <p className="text-gray-600 leading-relaxed">{pkg.description}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline space-x-2">
              <span className="text-4xl font-bold text-gray-900">
                {formatPriceWithoutCents(pkg.price)}
              </span>
              <span className="text-gray-600">/yıllık</span>
            </div>

            {pkg.originalPrice && (
              <div className="flex items-center space-x-2">
                <span className="text-lg text-gray-400 line-through">
                  {formatPriceWithoutCents(pkg.originalPrice)}
                </span>
                {discount > 0 && (
                  <Badge variant="destructive" className="animate-bounce">
                    %{discount} İndirim
                  </Badge>
                )}
              </div>
            )}

          </div>
        </CardHeader>

        <CardContent className="space-y-6 flex flex-col flex-1">
          <div className="space-y-3 flex-grow">
            <h4 className="font-semibold text-gray-800">Özellikler:</h4>
            {normalizedFeatures.map((feature, idx) => {
              const isIncluded = feature.included !== false;
              const featureText = typeof feature === 'string' ? feature : feature?.text || feature?.name || String(feature);
              return (
                <div
                  key={idx}
                  className="flex items-center space-x-3 transition-all duration-300"
                  style={{
                    animationDelay: `${index * 0.1 + idx * 0.05}s`,
                    animation: isHovered ? 'slideInRight 0.3s ease-out forwards' : 'none'
                  }}
                >
                  {isIncluded ? (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <XIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                  <span className={`text-sm font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {featureText}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="mt-auto pt-4">
            <Button className="w-full h-12 font-semibold transition-all duration-300 transform hover:scale-105 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white">
              <span>PLANI SEÇ</span>
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </CardContent>

        {isHovered && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-emerald-400 rounded-full animate-ping"></div>
            <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-green-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-teal-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-green-50">
      {hasHeroContent && (
        <section
          className="relative bg-gradient-to-br from-emerald-600 via-emerald-700 to-green-700 py-20 overflow-hidden"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 left-10 w-64 h-64 bg-emerald-200 rounded-full opacity-20 blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-300 rounded-full opacity-15 blur-3xl"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 text-white text-center lg:text-left">
                {derivedContent.hero.badge && (
                  <Badge className="bg-emerald-600 text-white px-4 py-2 text-lg mx-auto lg:mx-0">
                    {derivedContent.hero.badge}
                  </Badge>
                )}
                {(derivedContent.hero.title || derivedContent.hero.highlight) && (
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                    {derivedContent.hero.title}
                    {derivedContent.hero.highlight && (
                      <span className="block text-emerald-200 text-xl md:text-2xl">
                        {derivedContent.hero.highlight}
                      </span>
                    )}
                  </h1>
                )}
                {derivedContent.hero.description && (
                  <p className="text-xl md:text-2xl text-emerald-100 max-w-2xl mx-auto lg:mx-0">
                    {derivedContent.hero.description}
                  </p>
                )}
                {heroBullets.length > 0 && (
                  <div className="grid grid-cols-2 gap-4 justify-items-center lg:justify-items-start">
                    {heroBullets.map((bullet, idx) => {
                      const BulletIcon = resolveIcon(bullet.icon, 'CheckCircle');
                      return (
                        <div key={idx} className="text-emerald-100 flex items-center gap-2">
                          <BulletIcon className="w-5 h-5 text-emerald-200" />
                          <span>{bullet.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {derivedContent.hero.image_url && (
                <div className="hidden lg:block">
                  <div className="relative">
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8">
                      <MediaDisplay
                        src={derivedContent.hero.image_url}
                        alt={derivedContent.hero.title || 'E-Ticaret Hosting'}
                        className="w-full rounded-xl"
                        fallbackSrc={DEFAULT_CONTENT.hero.image_url}
                        videoProps={{ controls: true }}
                        imgProps={{
                          loading: "lazy",
                          onError: (e) => {
                            e.currentTarget.src = DEFAULT_CONTENT.hero.image_url;
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section
        className="py-20 bg-white"
        style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {showPackagesHeader && (
            <div className="text-center mb-12">
              {derivedContent.packages_section.title && (
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  {derivedContent.packages_section.title}
                </h2>
              )}
              {derivedContent.packages_section.description && (
                <p className="text-lg text-gray-600">{derivedContent.packages_section.description}</p>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading ? (
              <div className="col-span-4 flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              (packages.length ? packages : fallbackPackages).map((pkg, idx) => (
                <PackageCard key={pkg.id || pkg.code || idx} pkg={pkg} index={idx} />
              ))
            )}
          </div>
        </div>
      </section>

      {hasPosSection && (
        <section
          className="bg-gray-50 py-20"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {posCards.map((card, idx) => (
                <Card key={idx} className="p-8 text-center">
                  {card.image_url && (
                    <MediaDisplay
                      src={card.image_url}
                      alt={card.title}
                      className="mx-auto h-20 w-auto"
                      videoProps={{ controls: true }}
                      imgProps={{ loading: "lazy" }}
                    />
                  )}
                  {card.title && <h3 className="text-2xl font-bold mt-6">{card.title}</h3>}
                  {card.description && <p className="text-gray-600 mt-3">{card.description}</p>}
                  {card.secondary_description && <p className="text-gray-600 mt-2">{card.secondary_description}</p>}
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {hasHighlightsSection && (
        <section
          className="py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              {derivedContent.highlights_section.title && (
                <h2 className="text-3xl md:text-4xl font-bold">{derivedContent.highlights_section.title}</h2>
              )}
              {derivedContent.highlights_section.subtitle && (
                <p className="text-lg text-blue-100">{derivedContent.highlights_section.subtitle}</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {highlightCards.map((card, idx) => {
                const CardIcon = resolveIcon(card.icon, 'ShoppingCart');
                return (
                  <Card key={idx} className="p-6 bg-white/5 border-white/10">
                    {card.title && <h3 className="text-xl font-semibold mb-2 text-white">{card.title}</h3>}
                    {card.description && <p className="text-blue-100">{card.description}</p>}
                    <CardIcon className="w-10 h-10 text-emerald-300 mt-4" />
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section
        className="py-20 bg-gradient-to-b from-gray-50 to-gray-100"
        style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {derivedContent.faq_section.title}{' '}
              <span className="text-emerald-600">Sorular</span>
            </h2>
            {derivedContent.faq_section.subtitle && (
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">{derivedContent.faq_section.subtitle}</p>
            )}
          </div>
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {(faq.length ? faq : fallbackFaq).map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center">
                      <HelpCircle className="w-5 h-5 mr-3 text-emerald-600" />
                      {item.q}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {hasCTASection && (
        <section
          className="py-20 bg-gradient-to-r from-emerald-600 to-green-600 text-white"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              {derivedContent.cta_section.title && (
                <h2 className="text-4xl font-bold mb-4">{derivedContent.cta_section.title}</h2>
              )}
              {derivedContent.cta_section.description && (
                <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
                  {derivedContent.cta_section.description}
                </p>
              )}
              {(showPrimaryButton || showSecondaryButton) && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  {showPrimaryButton && primaryButton && (() => {
                    const PrimaryIcon = resolveIcon(primaryButton.icon, 'Mail');
                    const link = primaryButton.link;
                    return (
                      <a
                        href={link}
                        key="primary"
                        target={link.startsWith('/') ? undefined : '_blank'}
                        rel={link.startsWith('/') ? undefined : 'noopener noreferrer'}
                        className="inline-flex items-center px-8 py-4 bg-white text-emerald-600 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                      >
                        <PrimaryIcon className="w-5 h-5 mr-2" />
                        {primaryButton.text}
                      </a>
                    );
                  })()}

                  {showSecondaryButton && secondaryButton && (() => {
                    const SecondaryIcon = resolveIcon(secondaryButton.icon, 'MessageCircle');
                    const isWhatsapp = secondaryButton.action === 'whatsapp';
                    const phone = secondaryButton.phone || whatsappNumber;
                    const message = secondaryButton.prefill_message || '';
                    const link = isWhatsapp
                      ? `https://api.whatsapp.com/send/?phone=${encodeURIComponent(phone)}${
                          message ? `&text=${encodeURIComponent(message)}` : ''
                        }`
                      : secondaryButton.link;
                    return (
                      <a
                        key="secondary"
                        href={link || '#'}
                        target={isWhatsapp || (link && !link.startsWith('/')) ? '_blank' : undefined}
                        rel={isWhatsapp || (link && !link.startsWith('/')) ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
                      >
                        <SecondaryIcon className="w-5 h-5 mr-2" />
                        {secondaryButton.text}
                      </a>
                    );
                  })()}
                </div>
              )}
              {ctaHighlights.length > 0 && (
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
                  {ctaHighlights.map((highlight, idx) => {
                    const HighlightIcon = resolveIcon(highlight.icon, 'Clock');
                    return (
                      <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center space-y-2">
                        <HighlightIcon className="w-12 h-12 mx-auto mb-4" />
                        {highlight.title && <h3 className="font-semibold text-lg">{highlight.title}</h3>}
                        {highlight.description && <p className="text-emerald-100">{highlight.description}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default EcommerceHostingPage;


